import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import Feedback from './Feedback';
import SingleChoice from '../question/SingleChoice';
import MultipleChoice from '../question/MultipleChoice';
import { mislecAPI } from '../../../../util/axios';
import Iconify from '../../../../theme/iconify/iconify';
import QuizProvider from '../../../../context/QuizContext';
import { questionTypeList } from '../../../util/form-values';
import { useResponsive } from '../../../../hooks/use-responsive';

function Quiz({ user, updateUser, quiz, courseId, sectionId, subsectionId }) {
    const { t } = useTranslation();
    const mdUp = useResponsive('up', 'md');

    const explain = async (question, answer) => {
        //  Data preparation
        const data = {
            question: question.question,
            correct_answers: question.answers.filter((a) => a.is_correct).map((a) => a.text),
            chosen_answer: answer,
            language: user.language,
            course_id: courseId,
            section_id: sectionId
        };

        //  Send data to the server
        try {
            const res = mislecAPI.post('/api/course/quiz/explain', data);
            return (await res).data.explanation;
        } catch (error) {
            //  Expired session
            if (error?.response)
                if (error?.response?.data?.detail === 'api.auth.verify.401')
                    updateUser(null);
            return null;
        }
    }

    const saveQuiz = (question, answers, explanations) => {
        if (!explanations) return;

        //  Data  preparation
        const data = {
            course_id: courseId,
            section_id: sectionId,
            quiz: {
                id: subsectionId,
                questions: [{
                    id: question.id,
                    answers: question.answers
                        .map((a, index) => ({ id: a.id, explanation: explanations[index]?.explanation ? explanations[index].explanation : null }))
                        .filter((a) => answers.includes(a.id))
                }]
            }
        };

        if (quiz.questions.some((question) => user.courses.find((c) => c.id === courseId)
            ?.quiz?.find((q) => q.id === subsectionId)
            ?.questions?.find((q) => q.id === question.id)
            ?.answers?.length > 0)) data.quiz.feedback = null;

        //  Send data to the server
        mislecAPI.put(`/api/users/${user._id}/quiz`, data)
            .then((res) => updateUser(res.data.user))
            .catch((error) => {
                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    return (
        <>
            <Stack spacing={2} sx={{ mt: 2, mb: 1, zIndex: 1 }}>
                {(quiz.type !== 'exam' || user.role !== 'student' || (user.courses.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving)) &&
                    <>
                        <Grid container>
                            <Grid item xs={12} md={!user.courses.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving ? 12 : 10}>
                                <Grid spacing={2} container>
                                    {quiz.questions.map((question, index) => (
                                        <Grid item xs={12} key={index}>
                                            <Stack key={index} sx={{ border: '1px solid rgba(33, 43, 54, 0.12)', borderRadius: '8px' }}>
                                                <Grid container sx={{ backgroundColor: '#F5F6F6', borderRadius: '8px 8px 0px 0px', p: 0.5, borderBottom: '1px solid rgba(33, 43, 54, 0.12)' }}>
                                                    <Grid item xs={12}>
                                                        <Grid container alignItems='center'>
                                                            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }}>
                                                                <Typography variant='h6' sx={{ fontSize: '0.9rem !important', textAlign: 'center', ml: 2, mr: 1.25 }}>
                                                                    {mdUp && `${t('course.modals.newQuiz.questions.question')} ${index + 1}`}
                                                                    {!mdUp && `${t('course.modals.newQuiz.questions.question')[0]}${index + 1}`}
                                                                </Typography>
                                                                <Divider orientation='vertical' flexItem sx={{ my: 0.5, mx: 1 }} />
                                                                <Tooltip title={t('course.modals.newQuiz.questions.tooltips.questionType')} arrow>
                                                                    <IconButton
                                                                        sx={{
                                                                            borderRadius: '8px',
                                                                            p: 0.5,
                                                                            px: 1,
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(145, 158, 171, 0.08)',
                                                                            },
                                                                        }}
                                                                        disableFocusRipple
                                                                        disableRipple
                                                                    >
                                                                        <Iconify icon={questionTypeList.filter((qt) => qt.type === question?.type)[0].icon} width={18} sx={{ color: 'primary.main' }} />
                                                                        <Typography variant='body2' sx={{ ml: 0.5, color: 'text.primary', fontSize: '0.8rem' }}>
                                                                            {t(`course.modals.newQuiz.questions.type.${question?.type}`)}
                                                                        </Typography>
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Grid>
                                                            {mdUp &&
                                                                <Grid item md={6} sx={{ display: 'flex', justifyContent: 'right', p: 1 }}>
                                                                    <IconButton
                                                                        disabled
                                                                        sx={{
                                                                            pt: '4px',
                                                                            pb: '4px',
                                                                            pr: '4px',
                                                                            pl: '4px',
                                                                            borderRadius: '8px',
                                                                            visibility: 'hidden'
                                                                        }}
                                                                    >
                                                                        <Iconify icon='octicon:move-to-start-16' />
                                                                    </IconButton>
                                                                </Grid>
                                                            }
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid container flexDirection='column' justifyContent='flex-start' sx={{ p: '16px', backgroundColor: '#FFF', borderRadius: '0px 0px 16px 16px' }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant='body1' sx={{ fontSize: '0.975rem' }}>
                                                            {question.question}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack spacing={1} sx={{
                                                        mt: 1,
                                                        pl: {
                                                            md: 2,
                                                            xs: 0
                                                        }
                                                    }}>
                                                        <QuizProvider>
                                                            {question.type === 'singleChoice' && <SingleChoice user={user} quiz={quiz} question={question} explain={explain} saveQuiz={saveQuiz} courseId={courseId} subsectionId={subsectionId} />}
                                                            {question.type === 'multipleChoice' && <MultipleChoice user={user} quiz={quiz} question={question} explain={explain} saveQuiz={saveQuiz} courseId={courseId} subsectionId={subsectionId} />}
                                                        </QuizProvider>
                                                    </Stack>
                                                </Grid>
                                            </Stack>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                        {user.role === 'student' && <Feedback user={user} updateUser={updateUser} courseId={courseId} sectionId={sectionId} subsectionId={subsectionId} />}
                    </>
                }
            </Stack>
        </>
    );
}

export default Quiz;